import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import { CreditCard, Check, X, Plus, AlertTriangle, RefreshCw, Edit, GraduationCap, Clock, Trash2, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Link } from "react-router-dom";
import { API } from "../../App";

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [mensalidades, setMensalidades] = useState([]);
  const [editingSub, setEditingSub] = useState(null);
  const [editForm, setEditForm] = useState({ monthly_price: 30, weekly_credits: 2 });
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Student picker state
  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [studentSearching, setStudentSearching] = useState(false);
  const studentDebounceRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [subsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/services`, { withCredentials: true }),
      ]);
      setSubscriptions(subsRes.data);
      setMensalidades(servicesRes.data.filter(s => s.category === "mensalidade"));
    } catch { toast.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  };

  const searchStudents = useCallback(async (query) => {
    if (!query || query.length < 2) { setStudentResults([]); return; }
    setStudentSearching(true);
    try {
      const res = await axios.get(`${API}/admin/students?page=1&limit=8&search=${encodeURIComponent(query)}`, { withCredentials: true });
      const subscribedIds = new Set(subscriptions.map(s => s.user_id));
      setStudentResults(res.data.students.filter(s => !subscribedIds.has(s.user_id)));
    } catch { setStudentResults([]); }
    finally { setStudentSearching(false); }
  }, [subscriptions]);

  const handleStudentSearchChange = (value) => {
    setStudentSearch(value);
    clearTimeout(studentDebounceRef.current);
    studentDebounceRef.current = setTimeout(() => searchStudents(value), 300);
  };

  const handleAddSubscription = async () => {
    if (!selectedStudent) return;
    try {
      const payload = { user_id: selectedStudent.user_id };
      if (selectedPlan) payload.plan_id = selectedPlan;
      await axios.post(`${API}/subscriptions`, payload, { withCredentials: true });
      toast.success("Subscrição criada com sucesso");
      setShowAddDialog(false);
      setSelectedStudent(null);
      setSelectedPlan("");
      setStudentSearch("");
      setStudentResults([]);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar subscrição"); }
  };

  const handleMarkPayment = async (subId, month, status) => {
    try {
      await axios.post(`${API}/subscriptions/${subId}/payments/${month}/mark`, { status }, { withCredentials: true });
      toast.success(status === "paid" ? "Pagamento marcado como pago" : "Pagamento revertido");
      fetchData();
    } catch { toast.error("Erro ao marcar pagamento"); }
  };

  const handleUpdateSub = async () => {
    if (!editingSub) return;
    try {
      await axios.put(`${API}/subscriptions/${editingSub}`, editForm, { withCredentials: true });
      toast.success("Subscrição atualizada");
      setEditingSub(null);
      fetchData();
    } catch { toast.error("Erro ao atualizar"); }
  };

  const handleCancelSub = async (subId) => {
    try {
      await axios.delete(`${API}/subscriptions/${subId}`, { withCredentials: true });
      toast.success("Subscrição cancelada");
      fetchData();
    } catch { toast.error("Erro ao cancelar"); }
  };

  const currentMonth = new Date().toISOString().slice(0, 7);

  const filteredSubscriptions = subscriptions.filter(sub => {
    const paymentStatus = sub.current_payment?.status || "pending";
    const isOverdue = paymentStatus === "overdue" || (paymentStatus === "pending" && new Date().getDate() > 15);
    if (filterStatus === "paid" && (paymentStatus !== "paid" || sub.status === "cancelling")) return false;
    if (filterStatus === "overdue" && !isOverdue) return false;
    if (filterStatus === "cancelling" && sub.status !== "cancelling") return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!sub.user?.name?.toLowerCase().includes(q) && !sub.user?.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#6697a4] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#3d5a63] mb-1">Subscrições</h1>
          <p className="text-sm text-[#475569]">Gestão de subscrições mensais dos alunos</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#6697a4] hover:bg-[#3d5a63] rounded-full" data-testid="add-subscription-btn">
              <Plus className="mr-2 h-4 w-4" /> Nova Subscrição
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Subscrição Mensal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Student Picker */}
              <div>
                <Label>Aluno</Label>
                {selectedStudent ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg mt-1">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={selectedStudent.picture} />
                      <AvatarFallback className="bg-[#6697a4] text-white text-sm">{selectedStudent.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#3d5a63] truncate">{selectedStudent.name}</p>
                      <p className="text-xs text-[#94A3B8] truncate">{selectedStudent.email}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedStudent(null); setStudentSearch(""); setStudentResults([]); }} className="h-6 w-6 p-0">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                      <Input
                        placeholder="Pesquisar aluno por nome, email..."
                        value={studentSearch}
                        onChange={(e) => handleStudentSearchChange(e.target.value)}
                        className="pl-10"
                        data-testid="sub-student-search"
                      />
                    </div>
                    {studentSearching && <p className="text-xs text-[#94A3B8] mt-1">A pesquisar...</p>}
                    {studentResults.length > 0 && (
                      <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                        {studentResults.map(s => (
                          <button key={s.user_id} onClick={() => { setSelectedStudent(s); setStudentResults([]); setStudentSearch(""); }} className="flex items-center gap-2 w-full p-2.5 hover:bg-slate-50 transition-colors text-left" data-testid={`sub-pick-${s.user_id}`}>
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={s.picture} />
                              <AvatarFallback className="bg-[#6697a4] text-white text-xs">{s.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[#3d5a63] truncate">{s.name || s.email}</p>
                              <p className="text-xs text-[#94A3B8] truncate">{s.email}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {studentSearch.length >= 2 && !studentSearching && studentResults.length === 0 && (
                      <p className="text-xs text-[#94A3B8] mt-2">Nenhum aluno encontrado</p>
                    )}
                  </div>
                )}
              </div>
              {/* Plan Picker */}
              {mensalidades.length > 0 && (
                <div>
                  <Label>Plano (opcional)</Label>
                  <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                    <SelectTrigger data-testid="sub-plan-select"><SelectValue placeholder="Plano padrão" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Plano padrão</SelectItem>
                      {mensalidades.map(m => (
                        <SelectItem key={m.service_id} value={m.service_id}>{m.name} — {m.price}€/{m.weekly_credits || 0} cred/sem</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button onClick={handleAddSubscription} disabled={!selectedStudent} className="w-full bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="confirm-add-subscription">
                Confirmar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats — clickable filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { key: "all", icon: GraduationCap, color: "purple", count: subscriptions.length, label: "Todas" },
          { key: "paid", icon: Check, color: "green", count: subscriptions.filter(s => s.current_payment?.status === "paid" && s.status !== "cancelling").length, label: "Em Dia" },
          { key: "cancelling", icon: Clock, color: "amber", count: subscriptions.filter(s => s.status === "cancelling").length, label: "A Cancelar" },
          { key: "overdue", icon: X, color: "red", count: subscriptions.filter(s => s.current_payment?.status === "overdue" || (s.current_payment?.status === "pending" && new Date().getDate() > 15)).length, label: "Em Atraso" },
        ].map(stat => (
          <Card 
            key={stat.key} 
            className={`border-0 shadow-sm rounded-xl cursor-pointer transition-all hover:shadow-md ${filterStatus === stat.key ? `ring-2 ring-${stat.color}-400` : ""}`}
            onClick={() => setFilterStatus(filterStatus === stat.key ? "all" : stat.key)}
            data-testid={`sub-filter-${stat.key}`}
          >
            <CardContent className="p-5 text-center">
              <div className={`w-10 h-10 rounded-xl ${stat.color === "green" ? "bg-green-100" : stat.color === "red" ? "bg-red-100" : stat.color === "amber" ? "bg-amber-100" : "bg-purple-100"} flex items-center justify-center mx-auto mb-2`}>
                <stat.icon className={`h-5 w-5 ${stat.color === "green" ? "text-green-600" : stat.color === "red" ? "text-red-600" : stat.color === "amber" ? "text-amber-600" : "text-purple-600"}`} />
              </div>
              <p className={`text-2xl font-bold ${stat.color === "green" ? "text-green-600" : stat.color === "red" ? "text-red-600" : stat.color === "amber" ? "text-amber-600" : "text-[#3d5a63]"}`}>{stat.count}</p>
              <p className="text-xs text-[#94A3B8] mt-1">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Input 
        placeholder="Pesquisar por nome ou email..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="max-w-md"
        data-testid="sub-search"
      />

      {/* Subscriptions list */}
      {filteredSubscriptions.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-16 text-center">
            <GraduationCap className="h-14 w-14 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-lg text-[#475569] font-medium mb-1">{searchQuery || filterStatus !== "all" ? "Nenhuma subscrição encontrada" : "Sem subscrições mensais"}</p>
            <p className="text-sm text-[#94A3B8]">{searchQuery || filterStatus !== "all" ? "Tenta ajustar os filtros" : "Adiciona uma nova subscrição para começar"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSubscriptions.map(sub => {
            const paymentStatus = sub.current_payment?.status || "pending";
            const isOverdue = paymentStatus === "overdue" || (paymentStatus === "pending" && new Date().getDate() > 15);
            return (
              <Card key={sub.subscription_id} className="border-0 shadow-sm rounded-xl" data-testid={`subscription-${sub.subscription_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={sub.user?.picture} />
                        <AvatarFallback className="bg-purple-100 text-purple-700 text-base">{sub.user?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <Link to={`/students/${sub.user_id}`} className="font-semibold text-[#3d5a63] hover:text-[#6697a4] hover:underline">
                          {sub.user?.name || sub.user?.email}
                        </Link>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <Badge className={sub.status === "cancelling" ? "bg-amber-100 text-amber-700 border-0 text-xs" : "bg-purple-100 text-purple-700 border-0 text-xs"}>
                            {sub.status === "cancelling" ? "A Cancelar" : "Ativa"}
                          </Badge>
                          <span className="text-sm text-[#475569]">{sub.monthly_price}€/mês</span>
                          <span className="text-sm text-[#475569]">{sub.weekly_credits} créd/sem</span>
                        </div>
                        <p className="text-xs text-[#94A3B8] mt-1">Desde {sub.start_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge className={`text-xs px-3 py-1 ${isOverdue ? "bg-red-100 text-red-700 border-0" : paymentStatus === "paid" ? "bg-green-100 text-green-700 border-0" : "bg-amber-100 text-amber-700 border-0"}`}>
                        {isOverdue ? "Em Atraso" : paymentStatus === "paid" ? "Pago" : "Pendente"} ({currentMonth})
                      </Badge>
                      {paymentStatus !== "paid" ? (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full" onClick={() => handleMarkPayment(sub.subscription_id, currentMonth, "paid")} data-testid={`mark-paid-${sub.subscription_id}`}>
                          <Check className="h-3 w-3 mr-1" /> Pago
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 rounded-full" onClick={() => handleMarkPayment(sub.subscription_id, currentMonth, "pending")} data-testid={`mark-unpaid-${sub.subscription_id}`}>
                          Reverter
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditingSub(sub.subscription_id); setEditForm({ monthly_price: sub.monthly_price, weekly_credits: sub.weekly_credits }); }} data-testid={`edit-sub-${sub.subscription_id}`}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300 rounded-full" onClick={() => handleCancelSub(sub.subscription_id)} data-testid={`cancel-sub-${sub.subscription_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingSub} onOpenChange={(open) => !open && setEditingSub(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Subscrição</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Valor mensal (€)</Label>
              <Input type="number" value={editForm.monthly_price} onChange={e => setEditForm({ ...editForm, monthly_price: e.target.value === '' ? '' : parseFloat(e.target.value) })} onBlur={e => setEditForm({ ...editForm, monthly_price: parseFloat(e.target.value) || 0 })} data-testid="edit-sub-price" />
            </div>
            <div>
              <Label>Créditos por semana</Label>
              <Input type="number" min={1} max={10} value={editForm.weekly_credits} onChange={e => setEditForm({ ...editForm, weekly_credits: e.target.value === '' ? '' : parseInt(e.target.value) })} onBlur={e => setEditForm({ ...editForm, weekly_credits: parseInt(e.target.value) || 1 })} data-testid="edit-sub-credits" />
            </div>
            <Button onClick={handleUpdateSub} className="w-full bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-sub-edit">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Activity Logs Component

export default AdminSubscriptions;
